import { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { isEmpty } from "@util/utils";
import useMessageDialog from "@util/hook/useMessageDialog";
import useCyclePurchaseApi from "@apis/useCyclePurchaseApi";

/**
 * 管理收件人常用資料的 Hook
 * 整合了表單資料管理和套用常用資料功能
 *
 * @param {number} memberID - 會員 ID
 * @param {Object} [initialSource] - 初始資料來源（可選，用於從 cycleOrder 初始化）
 * @returns {Object} 包含 formData 和 applyDefaultReceiver 函數
 */
const useMemberDefaultReceiver = (memberID, initialSource = {}) => {
	const setMessageDialog = useMessageDialog();
	const { getCyclePurchaseMemberReceiverInfoApi } = useCyclePurchaseApi();

	// 表單資料管理（整合 useFormDataWithKey）
	const [formData, setFormData] = useState({
		source: initialSource,
		key: uuid(),
	});

	// 快取已請求的資料
	const cachedDataRef = useRef(null);
	// 標記是否正在請求中
	const isLoadingRef = useRef(false);

	// 轉換收件人資料格式
	const transformReceiverData = useCallback(data => {
		if (isEmpty(data)) {
			return null;
		}

		return {
			receiverName: data.receiverName || "",
			receiverMobile: data.receiverMobile || "",
			receiverPostCode: data.receiverPostCode || "",
			receiverAddress: data.receiverAddress || "",
		};
	}, []);

	// 當 initialSource 變化時，更新 formData（用於從 cycleOrder 初始化）
	useEffect(
		() => {
			if (!isEmpty(initialSource)) {
				// 轉換初始資料格式
				const transformed = transformReceiverData(initialSource);
				if (transformed) {
					setFormData({
						source: transformed,
						key: uuid(),
					});
				} else {
					// 如果轉換失敗，仍然設置原始資料（可能來自 cycleOrder，格式不同）
					setFormData({
						source: {
							receiverName: initialSource.receiverName || "",
							receiverMobile: initialSource.receiverMobile || "",
							receiverPostCode: initialSource.receiverPostCode || "",
							receiverAddress: initialSource.receiverAddress || "",
						},
						key: uuid(),
					});
				}
			}
		},
		// eslint-disable-next-line
		[initialSource],
	);

	/**
	 * 套用常用收件人資料
	 */
	const applyDefaultReceiver = useCallback(
		async () => {
			// 檢查 memberID 是否存在且有效
			if (!memberID || memberID <= 0) {
				setMessageDialog({
					open: true,
					title: "提醒",
					msg: "請先選擇會員，謝謝。",
				});
				return;
			}

			// 如果正在請求中，不重複請求
			if (isLoadingRef.current) {
				return;
			}

			// 如果已經有快取的資料，直接套用
			if (cachedDataRef.current !== null) {
				const transformedData = transformReceiverData(cachedDataRef.current);

				if (isEmpty(transformedData)) {
					setMessageDialog({
						open: true,
						title: "提醒",
						msg: "很抱歉！無法取得相關資料，謝謝。",
					});
					return;
				}

				// 更新表單資料
				setFormData({
					source: transformedData,
					key: uuid(),
				});
				return;
			}

			// 開始請求
			isLoadingRef.current = true;

			try {
				const resp = await getCyclePurchaseMemberReceiverInfoApi(memberID);

				if (isEmpty(resp)) {
					setMessageDialog({
						open: true,
						title: "提醒",
						msg: "很抱歉！無法取得相關資料，謝謝。",
					});
					isLoadingRef.current = false;
					return;
				}

				const receiverData = resp;

				// 快取原始資料
				cachedDataRef.current = receiverData;

				// 轉換資料格式
				const transformedData = transformReceiverData(receiverData);

				// 更新表單資料
				setFormData({
					source: transformedData,
					key: uuid(),
				});
			} catch (error) {
				setMessageDialog({
					open: true,
					title: "提醒",
					msg: "很抱歉！無法取得相關資料，謝謝。",
				});
			} finally {
				isLoadingRef.current = false;
			}
		},
		// eslint-disable-next-line
		[memberID, getCyclePurchaseMemberReceiverInfoApi, transformReceiverData, setMessageDialog],
	);

	useEffect(() => {
		if (cachedDataRef.current) {
			// 重置快取資料
			cachedDataRef.current = null;

			// 重置表單資料
			setFormData({
				source: {},
				key: uuid(),
			});
		}
		// eslint-disable-next-line
	}, [memberID]);

	return {
		formData, // { source, key }
		applyDefaultReceiver,
	};
};

export default useMemberDefaultReceiver;
